import * as React from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { ProductPrice, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { ICartState } from '@msdyn365-commerce/global-state';
import { AddToCartComponent, IAddToCartToastResources } from '../../dlvry-buybox/common/add-to-cart.component';
import { ProductSearchResultExt, SimpleProductExt } from '../../../shared-utils/types';
import PQueue from 'p-queue';
import { BuyValidationResult } from '../../../themes/dlvry-adventureworks/views/components/product.component';
import { IDlvrySearchResources } from '../dlvry-search.props.autogenerated';
import QuantityInput from '../../../components/common/quantity-input';
import { getNonBreakoffUOM } from '../../../shared-utils/unit-conversion-utils';
import { LifecycleStatus } from '../../../shared-utils/lifecycle-status';
import { Availability } from '../../../components/dropdown/availability';
import CartCount from '../../../components/cart-count';

interface IBuySectionProps {
    context: ICoreContext;
    id: string;
    typeName: string;
    resources: IDlvrySearchResources;
    productRecordId: number;
    simpleProduct: SimpleProductExt | undefined;
    productPrice: ProductPrice | undefined;
    cart: ICartState;
    cartQueue: PQueue | undefined;
    inCartTooltipText: string | undefined;
    unitConversions: UnitOfMeasureConversion[] | undefined;
    availability: IProductInventoryInformation | undefined;
    onBuyValidate: (product: ProductSearchResultExt, quantity: number) => Promise<BuyValidationResult>;

    quantity: number,
    breakoff: boolean,
    onQuantityChange: (productRecordId: number, quantity: number) => void,
    onBreakoffChange: (productRecordId: number, breakoff: boolean) => void
    totalPrice?: React.ReactNode
}

const BuySection = (props: IBuySectionProps) => {
    const {
        id,
        typeName,
        context,
        resources,
        productRecordId,
        simpleProduct,
        productPrice,
        cart,
        cartQueue,
        quantity,
        breakoff,
        onQuantityChange,
        onBreakoffChange
    } = props;
    const [forceUpdate , setForceUpdate] = React.useState(0);

    // Prevents clicks inside the <a> element from navigating if on the quantity elements
    const onContainerClick = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const toastStrings: IAddToCartToastResources = {
        errorHeader: resources.toastErrorHeader,
        productsAddedToCart: resources.productsAddedToCart,
        productsRemovedFromCart: resources.productsRemovedFromCart,
        productsUpdatedInCart: resources.productsUpdatedInCart,
        goToCart: resources.toastGoToCart,
        noAccessToProduct: resources.toastNoAccessToProduct
    };

    const lifeCycleStatus = simpleProduct?.ExtensionProperties?.find(x => x.Key === 'PRODUCTLIFECYCLESTATEID')?.Value?.StringValue;
    const isOutOfSeason = lifeCycleStatus === LifecycleStatus.OutOfSeason;

    const productSearchResult: ProductSearchResultExt = {
        RecordId: productRecordId,
        Brand: simpleProduct?.Brand,
        Category: simpleProduct?.Category,
        CategoryPaths: simpleProduct?.CategoryPaths,
        ItemId: simpleProduct?.ItemId,
        Name: simpleProduct?.Name,
        Price: productPrice?.CustomerContextualPrice || 0
    };

    return (
        <div className='ms-search__buy-section' draggable onDragStart={e => e.preventDefault()} onClick={onContainerClick}>
            <div className='ms-search__buy-section__quantity'>
                {!isOutOfSeason && (
                    <QuantityInput
                        context={context}
                        product={simpleProduct}
                        extProduct={simpleProduct}
                        quantity={quantity}
                        breakoff={breakoff}
                        onQuantityChange={quantity => onQuantityChange(productRecordId, quantity)}
                        onBreakoffChange={breakoff => onBreakoffChange(productRecordId, breakoff)}
                        nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, props.unitConversions)}
                        unitConversions={props.unitConversions}
                    />
                )}

                {props.totalPrice}
            </div>

            <Availability
                context={context}
                baseClassName='ms-search'
                availability={props.availability}
            />

            {simpleProduct && (
                <div className='ms-search__buy-section__right'>
                    <CartCount
                        key={forceUpdate}
                        context={context}
                        cart={cart.cart}
                        product={simpleProduct}
                    />
                    <AddToCartComponent
                        id={id}
                        context={context}
                        typeName={typeName}
                        quantity={quantity}
                        breakoff={breakoff}
                        nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, props.unitConversions)}
                        onUpdateQuantity={quantity => onQuantityChange(productRecordId, quantity)}
                        data={{
                            cart,
                            product: simpleProduct,
                            price: productPrice,
                            productSearch: productSearchResult }}
                        cartQueue={cartQueue}
                        addToCartText={resources.addToCartButtonText}
                        removeFromCartText='WIP Fjern fra kurv'
                        updateCartText='WIP Oppdater'
                        expiredText={resources.expiredText}
                        outOfSeasonBuyButtonText={resources.outOfSeasonBuyButtonText}
                        toastStrings={toastStrings}
                        onBuyValidate={props.onBuyValidate}
                        onAdd={async () => { setForceUpdate(forceUpdate + 1); }}
                    />
                </div>
            )}
        </div>
    );
};

export default BuySection;
